/**
 * AC DataCenter — 공개(무로그인) 산업재해 사고사례 탐색기.
 * 작업 입력 → 유사 실제 사고사례(AI 정제·KO/EN) + 사고 경향 그래프 + 위험성평가 가입 게이트.
 * 서빙은 무LLM(정제본 캐시). 실행(위험성평가·JSA)은 가입 후 앱 내 기능.
 */
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  FiSearch, FiAlertTriangle, FiShield, FiArrowRight, FiActivity, FiRefreshCw,
} from 'react-icons/fi'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import Button from '../../components/ui/Button'
import Card, { CardBody } from '../../components/ui/Card'
import Spinner from '../../components/ui/Spinner'
import { cn } from '../../lib/utils'
import { trackView } from '../../lib/trackView'
import { AC_I18N, type AcLang } from './acI18n'
import { acSearch, acAggregate, type AcCase, type AcAggBucket, type AcAggregate } from './acDataCenterApi'
import ExampleShowcase from './ExampleShowcase'

const BAR_COLORS = ['#f59e0b', '#fb923c', '#fbbf24', '#f97316', '#facc15', '#ea580c', '#fdba74', '#d97706']
const SIGNUP_PATH = '/signup'

function useAcLang(): AcLang {
  const { i18n } = useTranslation()
  // /en/ 경로는 강제 영어 렌더(영어 SEO 프리렌더 대상). 그 외는 브라우저 언어(i18next).
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/en/')) return 'en'
  return i18n.language?.startsWith('en') ? 'en' : 'ko'
}

// ── 사고사례 카드 ─────────────────────────────────────────────────────────────
export function CaseCard({ c, s }: { c: AcCase; s: typeof AC_I18N['ko'] }) {
  return (
    <Card hover className="h-full">
      <CardBody className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-center justify-between gap-2">
          {c.category && (
            <span className="inline-flex items-center rounded-full bg-amber-500/12 px-2.5 py-1 text-xs font-semibold text-amber-400">
              {c.category}
            </span>
          )}
          {typeof c.similarity === 'number' && (
            <span className="text-[11px] tabular-nums text-ink-faint">
              {s.matchLabel} {Math.round(c.similarity * 100)}%
            </span>
          )}
        </div>
        <p className="text-[15px] leading-relaxed text-ink">{c.summary}</p>
        {c.hazard && (
          <div className="flex items-start gap-2 rounded-md bg-red-500/[0.06] px-3 py-2">
            <FiAlertTriangle className="mt-0.5 shrink-0 text-red-400" size={15} />
            <div className="text-[13px] leading-snug">
              <span className="font-semibold text-red-300">{s.hazardLabel}</span>
              <span className="text-ink-muted"> · {c.hazard}</span>
            </div>
          </div>
        )}
        {c.prevention && (
          <div className="mt-auto flex items-start gap-2 rounded-md bg-emerald-500/[0.06] px-3 py-2">
            <FiShield className="mt-0.5 shrink-0 text-emerald-400" size={15} />
            <div className="text-[13px] leading-snug">
              <span className="font-semibold text-emerald-300">{s.preventionLabel}</span>
              <span className="text-ink-muted"> · {c.prevention}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

// ── 분포 막대그래프 ───────────────────────────────────────────────────────────
function DistChart({ title, data }: { title: string; data: AcAggBucket[] }) {
  const rows = data.slice(0, 6).map((d) => ({ name: d.label, value: d.count }))
  if (!rows.length) return null
  return (
    <div className="rounded-lg border border-surface-elevated/40 bg-surface-card p-4">
      <div className="mb-2 text-sm font-semibold text-ink-muted">{title}</div>
      <ResponsiveContainer width="100%" height={Math.max(150, rows.length * 34)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 4, right: 16, top: 2, bottom: 2 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category" dataKey="name" width={120} tickLine={false} axisLine={false}
            tick={{ fill: 'rgba(200,200,205,0.85)', fontSize: 11 }}
          />
          <Tooltip
            cursor={{ fill: 'rgba(245,158,11,0.08)' }}
            contentStyle={{ background: '#1c1c20', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: '#e5e5e5' }}
          />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
            {rows.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

// ── 위험도 진단 (항목2 · 무LLM · 집계 기반) ──────────────────────────────────
export function RiskSnapshot({ agg, s, lang }: { agg: AcAggregate; s: typeof AC_I18N['ko']; lang: AcLang }) {
  const total = agg.sample_size || 1
  const top = agg.by_accident_type.slice(0, 3)
  if (!top.length) return null
  const topPct = Math.min(100, Math.round((top.reduce((a, b) => a + b.count, 0) / total) * 100))
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-5">
      <div className="flex items-center gap-2 text-sm font-bold text-amber-400">
        <FiActivity size={16} /> {s.diagTitle}
      </div>
      <p className="mt-1.5 text-sm text-ink-muted">
        {lang === 'en' ? <>About <b className="text-amber-300">{topPct}%</b> of related real accidents concentrate in these types.</>
          : <>관련 실제 사고의 약 <b className="text-amber-300">{topPct}%</b>가 아래 유형에 집중됩니다.</>}
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {top.map((d, i) => {
          const pct = Math.min(100, Math.round((d.count / total) * 100))
          return (
            <div key={i} className="rounded-lg border border-surface-elevated/40 bg-surface-card p-3.5">
              <div className="flex items-baseline justify-between">
                <span className="text-[11px] font-bold text-ink-faint">#{i + 1}</span>
                <span className="text-2xl font-extrabold tabular-nums text-amber-400">{pct}%</span>
              </div>
              <div className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm font-medium text-ink">{d.label}</div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-elevated/40">
                <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-400" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── "N건 더 있음 → 가입" 배너 ────────────────────────────────────────────────
function MoreCasesBanner({ s, relatedLabel }: { s: typeof AC_I18N['ko']; relatedLabel: string }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-surface-elevated/40 bg-surface-card px-5 py-4 sm:flex-row sm:justify-between">
      <p className="text-sm text-ink-muted">
        <FiActivity className="mr-1.5 inline text-amber-400" size={14} />
        {s.moreTitle(relatedLabel)}
      </p>
      <Link to={SIGNUP_PATH} className="w-full sm:w-auto">
        <Button variant="outline" size="sm" rightIcon={<FiArrowRight />} className="w-full sm:w-auto">
          {s.moreCta}
        </Button>
      </Link>
    </div>
  )
}

// ── 페이지 ────────────────────────────────────────────────────────────────────
export default function AcDataCenter() {
  const lang = useAcLang()
  const s = AC_I18N[lang]
  // 접속 비콘 — 사고사례 탐색 진입 1회(검색 사용량은 백엔드가 계측)
  useEffect(() => { trackView('ac-datacenter', 'visit') }, [])
  const [input, setInput] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [searchParams] = useSearchParams()

  // 공유·아카이브 링크(?q=)에서 검색어 자동 실행
  useEffect(() => {
    const q = searchParams.get('q')
    if (q && q.trim()) { setInput(q.trim()); setSubmitted(q.trim()) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 공개 유입 페이지 — 모바일 순단·일시 네트워크 오류에 관대하게(전역 1회 → 2회 재시도).
  const searchQ = useQuery({
    queryKey: ['acSearch', submitted, lang],
    queryFn: () => acSearch(submitted, lang),
    enabled: submitted.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })
  const aggQ = useQuery({
    queryKey: ['acAgg', submitted, lang],
    queryFn: () => acAggregate(submitted, lang),
    enabled: submitted.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const doSearch = (q: string) => {
    const v = q.trim()
    if (v) { setInput(v); setSubmitted(v) }
  }

  const result = searchQ.data
  const relatedLabel = result?.related_label ?? ''
  // 언어별 자기 URL을 canonical 로. hreflang 은 두 URL 을 실제로 분리(영어 SEO 인덱싱).
  const koUrl = 'https://heinrich.dasancms.co.kr/ac-datacenter/'
  const enUrl = 'https://heinrich.dasancms.co.kr/en/ac-datacenter/'
  const canonical = lang === 'en' ? enUrl : koUrl
  const jsonLd = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: s.metaTitle,
    description: s.metaDesc,
    url: canonical,
    keywords: ['산업재해', '사고사례', 'workplace accident', 'risk assessment', 'JSA'],
    isAccessibleForFree: true,
  }), [s])

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <html lang={lang} />
        <title>{s.metaTitle}</title>
        <meta name="description" content={s.metaDesc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="ko" href={koUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={koUrl} />
        <meta property="og:title" content={s.metaTitle} />
        <meta property="og:description" content={s.metaDesc} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* Hero + 검색 */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(60% 50% at 50% 0%, rgba(245,158,11,0.10), transparent 70%)' }}
        />
        <div className="relative mx-auto max-w-4xl px-4 pt-16 pb-8 text-center sm:pt-24">
          <motion.span
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-400"
          >
            <FiSearch size={12} /> {s.badge}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mt-5 text-3xl font-extrabold leading-tight text-ink sm:text-5xl"
          >
            {s.heroTitle}<br className="hidden sm:block" />{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              {s.heroHighlight}
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
            className="mx-auto mt-4 max-w-2xl text-base text-ink-muted sm:text-lg"
          >
            {s.heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.18 }}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/[0.06] px-4 py-1.5"
          >
            <span className="text-base font-extrabold text-amber-400 sm:text-lg">{s.heroStat}</span>
          </motion.div>

          <form
            onSubmit={(e) => { e.preventDefault(); doSearch(input) }}
            className="mx-auto mt-8 flex max-w-2xl flex-col gap-2 sm:flex-row"
          >
            <div className="relative flex-1">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink-faint" size={18} />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={s.searchPlaceholder}
                maxLength={200}
                className="w-full rounded-md border border-surface-elevated/50 bg-surface-card py-3.5 pl-11 pr-4 text-[15px] text-ink placeholder:text-ink-faint focus:border-amber-500/60 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              />
            </div>
            <Button type="submit" variant="primary" size="lg" isLoading={searchQ.isFetching} leftIcon={<FiSearch />}>
              {s.searchBtn}
            </Button>
          </form>

          {/* 예시 칩 */}
          <div className="mx-auto mt-4 flex max-w-2xl flex-wrap items-center justify-center gap-2">
            <span className="text-xs text-ink-faint">{s.examplesLabel}:</span>
            {s.examples.map((ex) => (
              <button
                key={ex} onClick={() => doSearch(ex)}
                className="rounded-full border border-surface-elevated/50 bg-surface-card px-3 py-1 text-xs text-ink-muted transition-colors hover:border-amber-500/40 hover:text-amber-400"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 결과 */}
      <section className="mx-auto max-w-6xl px-4 pb-24">
        {searchQ.isFetching && (
          <div className="flex flex-col items-center gap-3 py-20 text-ink-muted">
            <Spinner size="lg" />
            <p className="text-sm">{s.searching}</p>
          </div>
        )}

        {!searchQ.isFetching && submitted && result && result.cases.length === 0 && (
          <div className="py-20 text-center">
            <h3 className="text-lg font-semibold text-ink">{s.emptyTitle}</h3>
            <p className="mt-2 text-sm text-ink-muted">{s.emptyDesc}</p>
          </div>
        )}

        {!searchQ.isFetching && result && result.cases.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
            {/* 관련 건수 + 위험도 진단(항목2) + 분포 그래프 */}
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-xl font-bold text-ink sm:text-2xl">{s.relatedTitle(relatedLabel)}</h2>
                <span className="text-sm text-ink-faint">{s.relatedSub}</span>
              </div>
              {aggQ.data && aggQ.data.by_accident_type.length > 0 && (
                <div className="mt-4"><RiskSnapshot agg={aggQ.data} s={s} lang={lang} /></div>
              )}
              {aggQ.data && (aggQ.data.by_accident_type.length > 0 || aggQ.data.by_cause.length > 0) && (
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <DistChart title={s.byType} data={aggQ.data.by_accident_type} />
                  <DistChart title={s.byCause} data={aggQ.data.by_cause} />
                </div>
              )}
            </div>

            {/* 사고사례 카드 */}
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-faint">{s.casesTitle}</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {result.cases.map((c) => <CaseCard key={c.id} c={c} s={s} />)}
              </div>
              <div className="mt-4"><MoreCasesBanner s={s} relatedLabel={relatedLabel} /></div>
            </div>

            {/* 예시 RA/JSA/TBM 쇼케이스(항목3) */}
            <ExampleShowcase query={submitted} lang={lang} />

            {/* 면책 */}
            {result.disclaimer && (
              <p className="border-t border-surface-elevated/30 pt-5 text-xs leading-relaxed text-ink-faint">
                <span className="font-semibold">{s.disclaimerTitle}:</span> {result.disclaimer}
              </p>
            )}
          </motion.div>
        )}

        {searchQ.isError && (
          <div className="py-16 text-center">
            <p className="text-sm text-red-400">
              {lang === 'en'
                ? 'Connection issue — results could not load.'
                : '연결이 원활하지 않아 결과를 불러오지 못했어요.'}
            </p>
            {/* 순단·일시 네트워크 오류는 재검색 없이 그 자리에서 재시도할 수 있어야 한다. */}
            <Button
              variant="secondary" size="sm" className="mt-3"
              isLoading={searchQ.isFetching || aggQ.isFetching}
              leftIcon={<FiRefreshCw />}
              onClick={() => { searchQ.refetch(); aggQ.refetch() }}
            >
              {lang === 'en' ? 'Try again' : '다시 시도'}
            </Button>
          </div>
        )}
      </section>
    </>
  )
}
